/**
 * Compact binary encodings for transport and storage.
 *
 * Levels ("QTLV"): header (magic 4, version u8, station id length u8, station id bytes,
 * year u16, start f64 ms, interval u16 minutes, count u32) followed by count Int16 values
 * in cm; -32768 marks a missing value. About 105 KB per station-year before compression.
 *
 * Extrema ("QTEX"): same header shape, then per extremum: minute offset from start u32,
 * level Int16, type u8 (0 = LW, 1 = HW).
 */
const MISSING = -32768;
const enc = new TextEncoder();
const dec = new TextDecoder();
function writeHeader(magic, stationId, year, start, interval, count, payload) {
    const id = enc.encode(stationId);
    const headerSize = 4 + 1 + 1 + id.length + 2 + 8 + 2 + 4;
    const bytes = new Uint8Array(headerSize + payload);
    const view = new DataView(bytes.buffer);
    bytes.set(enc.encode(magic), 0);
    view.setUint8(4, 1);
    view.setUint8(5, id.length);
    bytes.set(id, 6);
    let o = 6 + id.length;
    view.setUint16(o, year);
    o += 2;
    view.setFloat64(o, start);
    o += 8;
    view.setUint16(o, interval);
    o += 2;
    view.setUint32(o, count);
    o += 4;
    return { view, bytes, offset: o };
}
function readHeader(bytes, magic) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (dec.decode(bytes.subarray(0, 4)) !== magic)
        throw new Error(`Not a ${magic} block`);
    if (view.getUint8(4) !== 1)
        throw new Error('Unsupported version');
    const idLen = view.getUint8(5);
    const stationId = dec.decode(bytes.subarray(6, 6 + idLen));
    let o = 6 + idLen;
    const year = view.getUint16(o);
    o += 2;
    const start = view.getFloat64(o);
    o += 8;
    const interval = view.getUint16(o);
    o += 2;
    const count = view.getUint32(o);
    o += 4;
    return { view, stationId, year, start, interval, count, offset: o };
}
export function encodeLevelSeries(s) {
    const { view, bytes, offset } = writeHeader('QTLV', s.stationId, s.year, s.start, s.intervalMinutes, s.values.length, s.values.length * 2);
    s.values.forEach((v, i) => view.setInt16(offset + i * 2, v === null ? MISSING : v));
    return bytes;
}
export function decodeLevelSeries(bytes) {
    const h = readHeader(bytes, 'QTLV');
    const values = new Array(h.count);
    for (let i = 0; i < h.count; i++) {
        const v = h.view.getInt16(h.offset + i * 2);
        values[i] = v === MISSING ? null : v;
    }
    return { stationId: h.stationId, year: h.year, start: h.start, intervalMinutes: h.interval, values };
}
export function encodeExtremaSeries(s) {
    const start = Date.UTC(s.year, 0, 1);
    const { view, bytes, offset } = writeHeader('QTEX', s.stationId, s.year, start, 1, s.extrema.length, s.extrema.length * 7);
    s.extrema.forEach((e, i) => {
        const o = offset + i * 7;
        view.setUint32(o, Math.round((e.time - start) / 60_000));
        view.setInt16(o + 4, e.level);
        view.setUint8(o + 6, e.type === 'HW' ? 1 : 0);
    });
    return bytes;
}
export function decodeExtremaSeries(bytes) {
    const h = readHeader(bytes, 'QTEX');
    const extrema = [];
    for (let i = 0; i < h.count; i++) {
        const o = h.offset + i * 7;
        extrema.push({ time: h.start + h.view.getUint32(o) * 60_000, level: h.view.getInt16(o + 4), type: h.view.getUint8(o + 6) === 1 ? 'HW' : 'LW' });
    }
    return { stationId: h.stationId, year: h.year, extrema };
}
//# sourceMappingURL=codec.js.map